/**
 * アクション選択スキップ Move（共通）
 *
 * 複数マップで共通して使用できるスキップ処理。
 * マップごとの完了フラグ設定・バリデーションはコールバックで対応。
 *
 * @example
 * // Phase定義での使用
 * import { createSkipAction } from '../moves/common/skipAction';
 *
 * export const skipAction = createSkipAction<MyGameState>({
 *   onComplete: (G) => { G.isActionSelectionComplete = true; },
 *   validate: (G, playerId) => {
 *     if (G.isActionSelectionComplete) return { valid: false, error: 'Already completed' };
 *     return { valid: true };
 *   }
 * });
 */

import { INVALID_MOVE } from 'boardgame.io/core';
import { debug } from '../../../utils/debug';
import type { Ctx } from 'boardgame.io';

/**
 * スキップ可能なゲーム状態の共通インターフェース
 */
export interface SkipActionGameState {
  players: Record<string, {
    selectedAction: string | null;
    hasPassed?: boolean;
  }>;
}

/**
 * バリデーション結果
 */
export interface SkipValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * skipAction 設定オプション
 */
export interface SkipActionOptions<G extends SkipActionGameState> {
  /** スキップ完了時に呼ばれるコールバック（完了フラグ設定など） */
  onComplete: (G: G, playerId: string) => void;
  /** バリデーション関数（オプション） */
  validate?: (G: G, playerId: string) => SkipValidationResult;
}

/**
 * skipAction Move ファクトリ
 *
 * @param options - 設定オプション
 * @returns skipAction Move 関数
 */
export function createSkipAction<G extends SkipActionGameState>(
  options: SkipActionOptions<G>
) {
  return function skipAction(
    { G, ctx }: { G: G; ctx: Ctx }
  ): void | typeof INVALID_MOVE {
    const playerId = ctx.currentPlayer;

    // バリデーション（設定されている場合）
    if (options.validate) {
      const validation = options.validate(G, playerId);
      if (!validation.valid) {
        debug.log(`[skipAction] Validation failed: ${validation.error}`);
        return INVALID_MOVE;
      }
    }

    const player = G.players[playerId];

    // アクション未選択のまま
    player.selectedAction = null;
    player.hasPassed = true;

    debug.log(`[skipAction] Player ${playerId} skipped action selection`);

    // マップ固有の完了処理
    options.onComplete(G, playerId);
  };
}
